import React from 'react'
import Text from '../../../../components/text/Text'
import {useDisplayNumber} from '../../../../utils/number'
import {ILeaderDetail} from '../../../../apis/leader/getLeaderDetail'
import {leaderAllTimeProfit, leaderMonthlyProfit} from '../../../../apis/leader/getLeader'
import {useColor, useTag} from '../../../../utils/etc'
import ContainerLayout from '../../../../components/layout/ContainerLayout'
import {useTime} from '../../../../utils/time'

interface IProps {
    className?: string
    leaderDetail?: ILeaderDetail
}

// admin/copy/leader/:id 가 주는 값만 사용한다.
// 잔고 / 수수료율 / 일·월 스코어 / 자산·포지션은 admin 응답에 없어 여기서 보여줄 수 없다
// (docs/admin-copy-api-requests.md 참고).
const LeaderInfoContainer: React.FC<IProps> = ({className, leaderDetail}) => {
    const {renderStateColor} = useTag()
    const {getNumberColor} = useColor()
    const {floorBalance, renderPercent} = useDisplayNumber()
    const {localTimeString} = useTime()

    const win = leaderDetail?.win ?? 0
    const loss = leaderDetail?.loss ?? 0
    const totalTrade = win + loss
    const winRate = totalTrade > 0 ? (win / totalTrade) * 100 : 0
    const copier = leaderDetail?.copier ?? 0
    const maxCopier = leaderDetail?.max_copier ?? 0

    const cell = (label: string, value: React.ReactNode) => (
        <div className={'space-y-[5px]'}>
            <Text className={'text-body3 text-font03'}>{label}</Text>
            {value}
        </div>
    )

    return (
        <ContainerLayout className={className} title={'Copy Leader Info'}>
            <div className={'grid grid-cols-5 gap-y-[20px] md:text-center sm:grid-cols-2 sm:gap-y-[15px]'}>
                {cell(
                    'All Time Profit',
                    <Text className={`text-ti1 ${getNumberColor(leaderAllTimeProfit(leaderDetail))}`}>
                        {renderPercent(leaderAllTimeProfit(leaderDetail))}
                    </Text>,
                )}
                {cell(
                    'Monthly Profit (30d)',
                    <Text className={`text-ti1 ${getNumberColor(leaderMonthlyProfit(leaderDetail))}`}>
                        {renderPercent(leaderMonthlyProfit(leaderDetail))}
                    </Text>,
                )}
                {cell(
                    'Copier',
                    <div className={'flex items-center space-x-[10px] md:justify-center'}>
                        <div className={'flex items-center space-x-[3px]'}>
                            <Text className={`text-ti1 ${getNumberColor(maxCopier - copier)}`}>{copier}</Text>
                            <Text className={'text-font03'}>/</Text>
                            <Text className={'text-ti1 text-font03'}>{maxCopier}</Text>
                        </div>
                        {maxCopier - copier <= 0 && <Text className={`${renderStateColor(true)}`}>Full</Text>}
                    </div>,
                )}
                {cell('Min Balance', <Text className={'text-ti1'}>{floorBalance(leaderDetail?.min_balance ?? 0)}</Text>)}
                {cell(
                    'Win Rate',
                    <div>
                        <Text className={`text-ti1 ${winRate > 50 ? 'text-font_green' : 'text-font_red'}`}>
                            {renderPercent(winRate, 100)}
                        </Text>
                        <Text className={'text-body3 text-font03'}>
                            {win}W / {loss}L
                        </Text>
                    </div>,
                )}
                {cell('MDD', <Text className={'text-ti1'}>{renderPercent(leaderDetail?.mdd ?? 0)}</Text>)}
                {cell('TPD', <Text className={'text-ti1'}>{leaderDetail?.tpd ?? '-'}</Text>)}
                {cell('Risk', <Text className={'text-ti1'}>{leaderDetail?.risk ?? '-'}</Text>)}
                {cell(
                    'Lead Start',
                    <Text className={'text-ti2'}>
                        {leaderDetail?.lead_start_date
                            ? new Date(leaderDetail?.lead_start_date).toLocaleString()
                            : '-'}
                    </Text>,
                )}
                {cell(
                    'Create At',
                    <Text className={'text-ti2'}>
                        {leaderDetail?.created_at ? localTimeString(leaderDetail?.created_at) : '-'}
                    </Text>,
                )}
                {cell(
                    'Key ID',
                    <Text className={'text-body3 break-all'}>{leaderDetail?.id || '-'}</Text>,
                )}
                {cell(
                    'Account ID',
                    <Text className={'text-body3 break-all'}>{leaderDetail?.account_id || '-'}</Text>,
                )}
            </div>
        </ContainerLayout>
    )
}

export default LeaderInfoContainer
